import { HierarchyUtils, ComponentIds } from '@inter-ikea-core/core';
import { PlannerEcsMultiSystem } from './System';
import EventComponent from '../components/EventComponent';
import { ProductType } from '../../types';
import { calculateLightDrivers } from '../../utils/drivers';
// When adding and removing lights from the scene this system will handle
// the amount of cords, drivers and a controler that should be added to the shopping cart
export class LightAccessoriesSystem extends PlannerEcsMultiSystem {
    constructor(coreApi, needControler) {
        super(coreApi, 'LightAccessoriesSystem', [
            EventComponent.id,
            ComponentIds.SOMETHING_IS_DRAGGED_COMPONENT_ID
        ]);
        this.tenWattDriver = {
            name: 'tenWattDriver',
            wattage: 10,
            productRef: '50356187',
            ports: 3
        };
        this.thirtyWattDriver = {
            name: 'thirtyWattDriver',
            wattage: 30,
            productRef: '60342656',
            ports: 9
        };
        this.powerSupplyCord3Point5Meters = '50446881';
        this.intermediateCord2meters = '30394699';
        this.oldControler = '70408595'; // TRÅDFRI
        this.controler = '20528128'; // RODRET
        // In some countries the articles above does not exists and we should try to use these ones instead
        this.anslutaPowerSupplyCord3Point5Meters = '50116593';
        this.anslutaIntermediateCord2meters = '50116729';
        this.nbrOfLightsInScene = 0;
        this.products = [];
        this.needControler = needControler;
    }
    onAdd(entity, newComponent) {
        if (newComponent.typeId === ComponentIds.SOMETHING_IS_DRAGGED_COMPONENT_ID) {
            if (HierarchyUtils.descendants(entity).filter(e => e.getComponent(ComponentIds.LIGHT_COMPONENT_ID)?.type === 'inner').length > 0) {
                // Update when inner lights are being dragged since they can autoadapt to different sizes
                this.updateLights();
            }
        }
        else if (newComponent.typeId === EventComponent.id &&
            newComponent.event === 'history') {
            this.checkLightsInScene();
        }
    }
    onUpdate(_entity, _oldComponent, newComponent) {
        if (newComponent.typeId === EventComponent.id &&
            ['load', 'history'].includes(newComponent.event)) {
            this.checkLightsInScene();
        }
    }
    onRemove() { }
    removeCurrentDriversAndCordsInScene() {
        const driversInScene = this.coreApi.entities.getByProductType(ProductType.LIGHT_DRIVER);
        const cordsInScene = this.coreApi.entities.getByProductType(ProductType.LIGHT_CORD);
        driversInScene.forEach(driver => this.coreApi.getServices().entityManager.disposeEntity(driver));
        cordsInScene.forEach(cord => this.coreApi.getServices().entityManager.disposeEntity(cord));
    }
    checkLightsInScene() {
        const currentLightsInScene = this.getLightsInScene();
        this.updateControler();
        this.nbrOfLightsInScene = currentLightsInScene.length;
        this.updateLights();
    }
    updateControler() {
        const controlersInScene = this.coreApi.entities.getByProductType(ProductType.LIGHT_CONTROLER);
        if (!this.needControler(this.coreApi) && controlersInScene.length > 0) {
            controlersInScene.forEach(controler => this.coreApi.getServices().entityManager.disposeEntity(controler));
        }
        else if (this.needControler(this.coreApi) && controlersInScene.length === 0) {
            this.createEntitiesInScene(1, this.oldControler, this.controler);
        }
    }
    updateLights() {
        // Remove previous drivers and cords that could be in the scene
        this.removeCurrentDriversAndCordsInScene();
        if (this.nbrOfLightsInScene) {
            const requiredDrivers = this.calculateNumberOfDrivers();
            const requiredCords = this.calculateNumberOfCords(requiredDrivers.tenWattDriver + requiredDrivers.thirtyWattDriver);
            this.generateRequiredEntities(requiredDrivers, requiredCords);
        }
    }
    generateRequiredEntities(drivers, cords) {
        this.createEntitiesInScene(drivers.tenWattDriver, this.tenWattDriver.productRef, this.thirtyWattDriver.productRef);
        this.createEntitiesInScene(drivers.thirtyWattDriver, this.thirtyWattDriver.productRef);
        this.createEntitiesInScene(cords.powerSupplyCable35, this.powerSupplyCord3Point5Meters, this.anslutaPowerSupplyCord3Point5Meters);
        this.createEntitiesInScene(cords.intermediateCable2, this.intermediateCord2meters, this.anslutaIntermediateCord2meters);
    }
    createEntitiesInScene(amount, productRef, backupRef) {
        const mainArticleExists = this.articleExistsInMarket(productRef);
        const backupArticleExists = backupRef && this.articleExistsInMarket(backupRef);
        const productRefToUse = mainArticleExists ? productRef : backupArticleExists ? backupRef : null;
        if (!productRefToUse)
            return;
        [...Array(amount)].forEach(() => {
            const entity = this.coreApi.getServices().productManager.createEntity(productRefToUse);
            if (entity)
                this.coreApi.entities.placementService.forcePlaceEntity(entity);
        });
    }
    calculateNumberOfDrivers() {
        let driversToUse = {
            tenWattDriver: 0,
            thirtyWattDriver: 0
        };
        const lights = this.getLightsInScene().map(light => Object.assign(light, { wattage: this.getWattage(light), ports: 1 }));
        const drivers = [this.tenWattDriver, this.thirtyWattDriver].map(d => Object.assign(d, { price: this.getArticlePrice(d.productRef) }));
        driversToUse = calculateLightDrivers(drivers, lights);
        return driversToUse;
    }
    calculateNumberOfCords(driverAmount) {
        const cordsToUse = {
            powerSupplyCable35: 0,
            intermediateCable2: 0
        };
        if (!driverAmount)
            return cordsToUse;
        cordsToUse.powerSupplyCable35 = 1;
        cordsToUse.intermediateCable2 = driverAmount - 1;
        return cordsToUse;
    }
    getLightsInScene() {
        return this.coreApi.entities.getByComponent(ComponentIds.LIGHT_COMPONENT_ID);
    }
    articleExistsInMarket(articleId) {
        return this.products.some(p => p.coreProductId === articleId);
    }
    getWattage(light) {
        const product = this.products.find((product) => product.coreProductId ===
            light.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)?.productRef);
        return product?.wattage || 0;
    }
    getArticlePrice(articleId) {
        return this.products.find((product) => product.coreProductId === articleId)
            ?.priceInformation.currentPrice;
    }
    setMarketProducts(marketProduct) {
        this.products = marketProduct;
    }
}
//# sourceMappingURL=LightAccessoriesSystem.js.map